import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { BaseCheckboxPropsOverrides, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid"
import { CheckboxProps, MenuItem, ListItemText } from "@mui/material"
import { useSearchParams } from "@/hooks/useSearchParams"
import { isEqual } from "lodash"
import moment from "moment"

import { DEFAULT_COVERAGE_OPTIONS } from "@/app/settings/analysis-groups/season/constants"
import Dialog from "@/components/dialog/Dialog"
import Checkbox, { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import DatePicker from "@/components/form/datePicker/DatePicker"
import Selector from "@/components/form/selector/Selector"
import Table from "@/components/table/Table"
import Pagination from "@/components/table/Pagination"
import Chip from "@/components/chip/Chip"
import { BaseRadio } from "@/components/form/radio/Radio"
import BasicButton from "@/components/button/BasicButton"
import { ActivitiesAnalysisStatus, ActivityType, MatchDayType } from "@/types/activity"
import { CheckboxColor } from "@/types/components/checkbox"
import { useSeasonWeekQuery } from "@/hooks/api/useSettingQuery"
import { useActivitiesAnalysisStatusQuery } from "@/hooks/api/useActivityQuery"
import { REPORT_STATUS_OBJECT } from "@/constants/report"
import { REPORT_DATE_FORMAT } from "@/constants/date"
import { REPORT_TYPE } from "@/app/report/common/constants"

import "./activitySelectorDialog.scss"

const ALL_MATCH_DAY_OPTIONS = Object.values(MatchDayType)
const NONE_SEASON_WEEK_ID = -1

const ACTIVITY_SELECTOR_DIALOG_DATE_WIDTH = 120
const ACTIVITY_SELECTOR_DIALOG_NAME_MIN_WIDTH = 200
const ACTIVITY_SELECTOR_DIALOG_PLAYER_COUNT_WIDTH = 80
const ACTIVITY_SELECTOR_DIALOG_TYPE_WIDTH = 140

type ActivitySelectorDialogProps = {
  type: REPORT_TYPE
  open: boolean
  setOpen: (open: boolean) => void
  setSelectedDateString: (dateString: string) => void
  setSelectedActivityChip: (chip: string) => void
  selectedDate: { start: string; end: string }
  setSelectedDate: ({ start, end }: { start: string; end: string }) => void
  selectedActivity: number[]
  setSelectedActivity: (activityList: number[]) => void
  /** 선수 계정으로 조회 중일 때의 idfAccount. ReportFilter 가 이미 넘기고 있으나
   *  현재 다이얼로그에서는 사용하지 않는다 (타입만 맞춰 둔다). */
  playerIdfAccount?: number
}

export function globalDate(date: string) {
  return moment(date).format(REPORT_DATE_FORMAT)
}

export default function ActivitySelectorDialog({
  type,
  open,
  setOpen,
  setSelectedDateString,
  setSelectedActivityChip,
  selectedDate,
  setSelectedDate,
  selectedActivity,
  setSelectedActivity,
}: ActivitySelectorDialogProps) {
  const { t } = useTranslation()
  const searchParams = useSearchParams()

  const idfActivity = useMemo(
    () => searchParams.get("idfActivity")?.split(",").map(Number),
    [searchParams],
  )

  // dateFilter 는 입력 중인 값(화면 표시·저장용), searchDateFilter 는 실제 조회에 쓰는 확정 값.
  // 날짜 필드는 키 입력 한 번마다 값이 확정되므로(MUI 는 타이핑도 accept 로 커밋한다),
  // 그대로 조회에 물리면 "2026" 을 치는 동안 0002/0020/0202 로도 조회가 나간다.
  // 포커스 아웃 또는 달력 선택으로 팝업이 닫힐 때, 값이 실제로 바뀐 경우에만 조회한다.
  const [dateFilter, setDateFilter] = useState<Date[]>([])
  const [searchDateFilter, setSearchDateFilter] = useState<Date[]>([])
  const [selectedActivityType, setSelectedActivityType] = useState<
    Array<keyof typeof ActivityType>
  >(DEFAULT_COVERAGE_OPTIONS.map((option) => option.value))
  const [selectedSeasonWeeks, setSelectedSeasonWeeks] = useState<number[]>([])
  const [selectedMatchDays, setSelectedMatchDays] = useState<MatchDayType[]>([
    ...ALL_MATCH_DAY_OPTIONS,
  ])
  const [activityList, setActivityList] = useState<
    Array<ActivitiesAnalysisStatus & { isDisabled: boolean }>
  >([])
  const [selectedActivityList, setSelectedActivityList] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  })
  const [tableLoading, setTableLoading] = useState(false)
  const isFirstRender = useRef(true)
  const selectedActivityListRef = useRef(selectedActivityList)

  const { data: seasonWeekData } = useSeasonWeekQuery()
  const activeSeasonWeeks = useMemo(
    () => seasonWeekData?.seasonWeeks?.filter((sw) => sw.active && sw.idfSeasonWeek) ?? [],
    [seasonWeekData],
  )

  const allSeasonWeekIds = useMemo(
    () => [...activeSeasonWeeks.map((sw) => sw.idfSeasonWeek as number), NONE_SEASON_WEEK_ID],
    [activeSeasonWeeks],
  )

  useEffect(() => {
    if (allSeasonWeekIds.length > 0 && selectedSeasonWeeks.length === 0) {
      setSelectedSeasonWeeks(allSeasonWeekIds)
    }
  }, [allSeasonWeekIds])

  /** 입력 중인 날짜를 조회 대상으로 확정한다 (달라진 게 없으면 조회를 다시 걸지 않는다) */
  const commitDateFilter = useCallback((next: Date[]) => {
    setSearchDateFilter((prev) => (isEqual(prev, next) ? prev : next))
  }, [])

  /** 코드에서 날짜를 세팅할 때는 입력값과 조회값을 함께 맞춘다 (다이얼로그 진입·초기화) */
  const applyDateFilter = useCallback(
    (next: Date[]) => {
      setDateFilter(next)
      commitDateFilter(next)
    },
    [commitDateFilter],
  )

  const getEndOfDay = (date: Date) => {
    const nextDay = new Date(date)
    nextDay.setDate(nextDay.getDate() + 1)
    nextDay.setHours(0, 0, 0, 0)

    return nextDay.valueOf()
  }

  const { data, isLoading } = useActivitiesAnalysisStatusQuery(
    {
      start: searchDateFilter[0]?.valueOf(),
      end: searchDateFilter[1] ? getEndOfDay(searchDateFilter[1]) : searchDateFilter[0]?.valueOf(),
    },
    searchDateFilter.length === 2,
  )

  useEffect(() => {
    selectedActivityListRef.current = selectedActivityList
  }, [selectedActivityList])

  useEffect(() => {
    if (selectedDate?.start && selectedDate?.end) {
      applyDateFilter([new Date(selectedDate.start), new Date(selectedDate.end)])
    } else {
      let newDate = new Date()
      if (type === REPORT_TYPE.DAILY) {
        newDate = new Date(newDate.setFullYear(newDate.getFullYear() - 1))
      } else {
        newDate = new Date(newDate.setDate(newDate.getDate() - 27))
      }
      applyDateFilter([newDate, new Date()])
    }
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    if (selectedDate?.start && selectedDate?.end && selectedActivity?.length) {
      applyDateFilter([new Date(selectedDate.start), new Date(selectedDate.end)])

      const currentIds = Array.from(selectedActivityListRef.current.ids).sort()
      const newIds = [...selectedActivity].sort()
      if (!isEqual(currentIds, newIds)) {
        setSelectedActivityList({
          type: "include",
          ids: new Set(selectedActivity),
        })
      }
    } else {
      let newDate = new Date()

      if (type === REPORT_TYPE.DAILY) {
        newDate = new Date(newDate.setFullYear(newDate.getFullYear() - 1))
      } else {
        newDate = new Date(newDate.setDate(newDate.getDate() - 27))
      }

      applyDateFilter([newDate, new Date()])
      setSelectedActivityType(DEFAULT_COVERAGE_OPTIONS.map((option) => option.value))
      setSelectedSeasonWeeks(allSeasonWeekIds)
      setSelectedMatchDays([...ALL_MATCH_DAY_OPTIONS])
      setActivityList([])
    }
  }, [open, selectedDate, selectedActivity, type])

  const newActivityList = useMemo(() => {
    if (!data) {
      return []
    }
    setTableLoading(true)
    let filtered = data.filter((activity) => selectedActivityType.includes(activity.type))

    const isAllSeasonWeeksSelected =
      allSeasonWeekIds.length > 0 &&
      allSeasonWeekIds.every((id) => selectedSeasonWeeks.includes(id))

    if (!isAllSeasonWeeksSelected) {
      const includeNone = selectedSeasonWeeks.includes(NONE_SEASON_WEEK_ID)
      const selectedWeekIds = selectedSeasonWeeks.filter((id) => id !== NONE_SEASON_WEEK_ID)
      const selectedWeeks = activeSeasonWeeks.filter(
        (sw) => sw.idfSeasonWeek && selectedWeekIds.includes(sw.idfSeasonWeek),
      )

      filtered = filtered.filter((activity) => {
        if (activity.idfSeasonWeek) {
          return selectedWeekIds.includes(activity.idfSeasonWeek)
        }

        if (includeNone) {
          return true
        }

        const activityDate = moment(activity.date).format("YYYY-MM-DD")

        return selectedWeeks.some(
          (sw) =>
            sw.startDate &&
            sw.endDate &&
            activityDate >= sw.startDate &&
            activityDate <= sw.endDate,
        )
      })
    }

    if (selectedMatchDays.length === 0) {
      filtered = []
    } else if (selectedMatchDays.length < ALL_MATCH_DAY_OPTIONS.length) {
      filtered = filtered.filter(
        (activity) => !activity.matchDay || selectedMatchDays.includes(activity.matchDay),
      )
    }

    return filtered.map((activity) => ({
      ...activity,
      isDisabled: activity.status !== "ANALYZED",
    }))
  }, [
    data,
    selectedActivityType,
    selectedSeasonWeeks,
    selectedMatchDays,
    activeSeasonWeeks,
    allSeasonWeekIds,
  ])

  useEffect(() => {
    if (!data) {
      return
    }

    setActivityList(newActivityList)
    setTableLoading(false)
    const availableActivityList = newActivityList.filter(
      (activity) => activity.status === "ANALYZED",
    )

    if (type === "daily") {
      if (idfActivity) {
        setSelectedActivityList({
          type: "include",
          ids: new Set(idfActivity),
        })
      } else if (isFirstRender.current && availableActivityList[0]?.idfActivity) {
        setSelectedActivityList({
          type: "include",
          ids: new Set([availableActivityList[0].idfActivity]),
        })
      }
    } else {
      setSelectedActivityList({
        type: "include",
        ids: new Set(availableActivityList.map((a) => a.idfActivity)),
      })
    }
  }, [newActivityList, type, idfActivity])

  useEffect(() => {
    if (!isFirstRender.current || idfActivity || open || dateFilter.length !== 2) {
      return
    }

    if (selectedActivityList.ids.size === 0) {
      return
    }

    const newIds = Array.from(selectedActivityList.ids) as number[]
    if (isEqual(selectedActivity, newIds)) {
      return
    }

    setSelectedStrings()
    setSelectedActivity(newIds)

    if (type === REPORT_TYPE.TREND) {
      setSelectedDate({
        start: globalDate(dateFilter[0].toString()),
        end: globalDate(dateFilter[1].toString()),
      })
    }
  }, [selectedActivityList, open, dateFilter, idfActivity, selectedActivity, type])

  const onSaveActivityFilter = () => {
    isFirstRender.current = false
    setSelectedStrings()

    const selectedActivityIds = Array.from(selectedActivityList.ids) as number[]

    if (type === REPORT_TYPE.DAILY && selectedActivityIds.length > 0) {
      const params = new URLSearchParams(window.location.search)
      params.set("idfActivity", selectedActivityIds[0].toString())

      params.delete("idfSession")
      params.delete("idfPlayer")
      params.delete("dailyReportType")

      window.history.replaceState({}, "", window.location.pathname + "?" + params.toString())
    } else if (type === REPORT_TYPE.TREND && selectedActivityIds.length > 0) {
      const params = new URLSearchParams(window.location.search)

      params.set("idfActivity", selectedActivityIds.join(",").toString())
      params.set("dateStart", globalDate(dateFilter[0].toString()))
      params.set("dateFinish", globalDate(dateFilter[1].toString()))

      params.delete("idfPlayer")

      window.history.replaceState({}, "", window.location.pathname + "?" + params.toString())
    } else {
      setSelectedActivity(selectedActivityIds)
    }

    setSelectedDate({
      start: globalDate(dateFilter[0].toString()),
      end: globalDate(dateFilter[1].toString()),
    })
    setOpen(false)
  }

  const setSelectedStrings = () => {
    if (type === REPORT_TYPE.DAILY) {
      setSelectedDateString(
        `${data?.find((item) => item.idfActivity === Array.from(selectedActivityList.ids)[0])?.date.split("T")[0]}`,
      )
      setSelectedActivityChip(
        data?.find((item) => item.idfActivity === Array.from(selectedActivityList.ids)[0])?.name ??
          "",
      )
    } else {
      setSelectedDateString(
        `${globalDate(dateFilter[0].toString())} ~ ${globalDate(dateFilter[1].toString())}`,
      )
      setSelectedActivityChip("")
    }
  }

  const columns: GridColDef[] = [
    {
      field: "date",
      renderHeader: () => t("date"),
      renderCell: (params) => globalDate(params.row.date.toString()),
      width: ACTIVITY_SELECTOR_DIALOG_DATE_WIDTH,
      sortable: false,
    },
    {
      field: "name",
      renderHeader: () => t("activityName"),
      renderCell: (params) => (
        <div className="activity-name-cell">
          <p>{params.row.name}</p>
          {params.row.status !== "ANALYZED" && (
            <Chip type="default-small" label={t(REPORT_STATUS_OBJECT[params.row.status])} />
          )}
        </div>
      ),
      flex: 1,
      minWidth: ACTIVITY_SELECTOR_DIALOG_NAME_MIN_WIDTH,
      sortable: false,
    },
    {
      field: "playerCount",
      renderHeader: () => t("player"),
      width: ACTIVITY_SELECTOR_DIALOG_PLAYER_COUNT_WIDTH,
      sortable: false,
    },
    {
      field: "type",
      renderHeader: () => t("type"),
      renderCell: (params) => t(ActivityType[params.row.type as keyof typeof ActivityType]),
      width: ACTIVITY_SELECTOR_DIALOG_TYPE_WIDTH,
      sortable: false,
    },
  ]

  const onRowClick = ({ row }: any) => {
    if (type === "daily") {
      setSelectedActivityList({
        ...selectedActivityList,
        ids: new Set([row.idfActivity]),
      })
    } else {
      setSelectedActivityList((prev) => {
        if (prev.ids.has(row.idfActivity)) {
          const updatedIds = new Set(prev.ids)
          updatedIds.delete(row.idfActivity)

          return {
            ...prev,
            ids: updatedIds,
          }
        } else {
          return {
            ...prev,
            ids: new Set([...prev.ids, row.idfActivity]),
          }
        }
      })
    }
  }

  const RadioSelectionElement = (props: CheckboxProps) => {
    const onSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
      const parentRow = (e.target as HTMLElement).closest("[data-id]")
      const id = parentRow?.getAttribute("data-id")

      if (!id) {
        return
      }

      const targetRow = activityList.find((row) => String(row.idfActivity) === id)
      if (!targetRow) {
        return
      }

      onRowClick({ row: targetRow })
    }

    return (
      <BaseRadio checked={props.checked} onChange={onSelectionChange} disabled={props.disabled} />
    )
  }

  const CheckboxSelectionElement = (props: CheckboxProps) => {
    type NewPropsOverrides = CheckboxProps &
      BaseCheckboxPropsOverrides & { slotProps: { [key: string]: any } }
    const isHeader = (props as NewPropsOverrides).slotProps?.htmlInput?.["aria-label"]?.includes(
      "all",
    )

    const activityIds = new Set(
      activityList
        .filter((activity) => !activity.isDisabled)
        .map((activity) => activity.idfActivity),
    )

    if (isHeader) {
      const isHeaderAllSelected =
        isEqual(selectedActivityList.ids, activityIds) && activityIds.size > 0
      const isHeaderIndeterminate =
        activityList
          .filter((activity) => !activity.isDisabled)
          .map((activity) => activity.idfActivity)
          .some((id) => selectedActivityList.ids.has(id)) && !isHeaderAllSelected

      const onHeaderChange = () => {
        if (isHeaderAllSelected && !isHeaderIndeterminate) {
          isFirstRender.current = false

          return setSelectedActivityList({
            ...selectedActivityList,
            ids: new Set(),
          })
        }

        isFirstRender.current = false

        return setSelectedActivityList({
          ...selectedActivityList,
          ids: new Set(
            activityList
              .filter((activity) => !activity.isDisabled)
              .map((activity) => activity.idfActivity),
          ),
        })
      }

      return (
        <BaseCheckbox
          checked={isHeaderAllSelected}
          indeterminate={isHeaderIndeterminate}
          onChange={onHeaderChange}
          disabled={props.disabled}
        />
      )
    } else {
      const onSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
        const parentRow = (e.target as HTMLElement).closest("[data-id]")
        const id = parentRow?.getAttribute("data-id")

        if (!id) {
          return
        }

        const targetRow = activityList.find((row) => String(row.idfActivity) === id)
        if (!targetRow) {
          return
        }

        onRowClick({ row: targetRow })
      }

      return (
        <BaseCheckbox
          checked={props.checked}
          onChange={onSelectionChange}
          disabled={props.disabled}
        />
      )
    }
  }

  const isDisabledApply = useMemo(() => {
    const isEmptySelection = !selectedActivityList.ids.size
    const isEqualSelection = isEqual(selectedActivity, Array.from(selectedActivityList.ids))

    return isEmptySelection || isEqualSelection || tableLoading || isLoading
  }, [selectedActivityList, selectedActivity, tableLoading, isLoading])

  return (
    <Dialog
      open={open}
      title={t("selectActivity")}
      maxWidth="900px"
      onClose={() => setOpen(false)}
      onConfirm={onSaveActivityFilter}
      footer={
        <>
          <BasicButton type="default-outlined" size="md" onClick={() => setOpen(false)}>
            {t("button.cancel")}
          </BasicButton>
          <BasicButton
            type="primary-contained"
            size="md"
            onClick={onSaveActivityFilter}
            disabled={isDisabledApply}>
            {t("button.apply")}
          </BasicButton>
        </>
      }>
      <div className="ReportFilterActivityDialogCont">
        <div className="activity-dialog-date">
          <p className="activity-dialog-label">{t("date")}</p>
          <div>
            <DatePicker
              value={dateFilter[0]}
              onAccept={(value) => value && setDateFilter([value, dateFilter[1]])}
              onClose={() => commitDateFilter(dateFilter)}
              onFieldBlur={() => commitDateFilter(dateFilter)}
              shouldDisableDate={(date) => date > dateFilter[1]}
              slotProps={{
                popper: {
                  sx: { zIndex: 1400 },
                },
              }}
            />
            {"~"}
            <DatePicker
              value={dateFilter[1]}
              onAccept={(value) => value && setDateFilter([dateFilter[0], value])}
              onClose={() => commitDateFilter(dateFilter)}
              onFieldBlur={() => commitDateFilter(dateFilter)}
              slotProps={{
                popper: {
                  sx: { zIndex: 1400 },
                },
              }}
            />
          </div>
        </div>
        <div className="activity-dialog-filters">
          <div className="activity-dialog-filter-item">
            <p className="activity-dialog-label">{t("activityType")}</p>
            <Selector
              type="select"
              multiple
              displayEmpty
              value={selectedActivityType}
              onChange={(_e: { target: { value: any } }) => {}}
              renderValue={() => {
                if (selectedActivityType.length === DEFAULT_COVERAGE_OPTIONS.length) {
                  return t("all")
                }

                return `${selectedActivityType.length} ${t("selected")}`
              }}
              MenuProps={{ sx: { zIndex: 1400 } }}>
              <MenuItem
                key="all-activity-type"
                onClick={() => {
                  if (selectedActivityType.length === DEFAULT_COVERAGE_OPTIONS.length) {
                    setSelectedActivityType([])
                  } else {
                    setSelectedActivityType(DEFAULT_COVERAGE_OPTIONS.map((o) => o.value))
                  }
                }}
                sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
                <Checkbox
                  checked={selectedActivityType.length === DEFAULT_COVERAGE_OPTIONS.length}
                  indeterminate={
                    selectedActivityType.length > 0 &&
                    selectedActivityType.length < DEFAULT_COVERAGE_OPTIONS.length
                  }
                  size="medium"
                  color={CheckboxColor.PLATINUM}
                />
                <ListItemText primary={t("all")} />
              </MenuItem>
              {DEFAULT_COVERAGE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (selectedActivityType.includes(option.value)) {
                      setSelectedActivityType(
                        selectedActivityType.filter((v) => v !== option.value),
                      )
                    } else {
                      setSelectedActivityType([...selectedActivityType, option.value])
                    }
                  }}>
                  <Checkbox checked={selectedActivityType.includes(option.value)} size="medium" />
                  <ListItemText primary={t(option.label)} />
                </MenuItem>
              ))}
            </Selector>
          </div>
          <div className="activity-dialog-filter-item">
            <p className="activity-dialog-label">{t("season")}</p>
            <Selector
              type="select"
              multiple
              displayEmpty
              value={selectedSeasonWeeks.map(String)}
              onChange={(_e: { target: { value: any } }) => {}}
              renderValue={() => {
                if (selectedSeasonWeeks.length === allSeasonWeekIds.length) {
                  return t("all")
                }

                return `${selectedSeasonWeeks.length} ${t("selected")}`
              }}
              MenuProps={{ sx: { zIndex: 1400 } }}>
              <MenuItem
                key="all-season-week"
                onClick={() => {
                  if (selectedSeasonWeeks.length === allSeasonWeekIds.length) {
                    setSelectedSeasonWeeks([])
                  } else {
                    setSelectedSeasonWeeks(allSeasonWeekIds)
                  }
                }}
                sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
                <Checkbox
                  checked={selectedSeasonWeeks.length === allSeasonWeekIds.length}
                  indeterminate={
                    selectedSeasonWeeks.length > 0 &&
                    selectedSeasonWeeks.length < allSeasonWeekIds.length
                  }
                  size="medium"
                  color={CheckboxColor.PLATINUM}
                />
                <ListItemText primary={t("all")} />
              </MenuItem>
              {activeSeasonWeeks.map((sw, index) => (
                <MenuItem
                  key={sw.idfSeasonWeek}
                  value={String(sw.idfSeasonWeek)}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    const id = sw.idfSeasonWeek as number
                    if (selectedSeasonWeeks.includes(id)) {
                      setSelectedSeasonWeeks(selectedSeasonWeeks.filter((v) => v !== id))
                    } else {
                      setSelectedSeasonWeeks([...selectedSeasonWeeks, id])
                    }
                  }}>
                  <Checkbox
                    checked={selectedSeasonWeeks.includes(sw.idfSeasonWeek as number)}
                    size="medium"
                  />
                  <ListItemText
                    primary={`Week ${index + 1}`}
                    secondary={sw.startDate && sw.endDate ? `${sw.startDate} ~ ${sw.endDate}` : ""}
                  />
                </MenuItem>
              ))}
              <MenuItem
                key="none"
                value={String(NONE_SEASON_WEEK_ID)}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if (selectedSeasonWeeks.includes(NONE_SEASON_WEEK_ID)) {
                    setSelectedSeasonWeeks(
                      selectedSeasonWeeks.filter((v) => v !== NONE_SEASON_WEEK_ID),
                    )
                  } else {
                    setSelectedSeasonWeeks([...selectedSeasonWeeks, NONE_SEASON_WEEK_ID])
                  }
                }}>
                <Checkbox
                  checked={selectedSeasonWeeks.includes(NONE_SEASON_WEEK_ID)}
                  size="medium"
                />
                <ListItemText primary={t("none")} />
              </MenuItem>
            </Selector>
          </div>
          <div className="activity-dialog-filter-item">
            <p className="activity-dialog-label">{t("matchDay")}</p>
            <Selector
              type="select"
              multiple
              displayEmpty
              value={selectedMatchDays}
              onChange={(_e: { target: { value: any } }) => {}}
              renderValue={() => {
                if (selectedMatchDays.length === ALL_MATCH_DAY_OPTIONS.length) {
                  return t("all")
                }

                return `${selectedMatchDays.length} ${t("selected")}`
              }}
              MenuProps={{ sx: { zIndex: 1400 } }}>
              <MenuItem
                key="all-match-day"
                onClick={() => {
                  if (selectedMatchDays.length === ALL_MATCH_DAY_OPTIONS.length) {
                    setSelectedMatchDays([])
                  } else {
                    setSelectedMatchDays([...ALL_MATCH_DAY_OPTIONS])
                  }
                }}
                sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
                <Checkbox
                  checked={selectedMatchDays.length === ALL_MATCH_DAY_OPTIONS.length}
                  indeterminate={
                    selectedMatchDays.length > 0 &&
                    selectedMatchDays.length < ALL_MATCH_DAY_OPTIONS.length
                  }
                  size="medium"
                  color={CheckboxColor.PLATINUM}
                />
                <ListItemText primary={t("all")} />
              </MenuItem>
              {ALL_MATCH_DAY_OPTIONS.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (selectedMatchDays.includes(option)) {
                      setSelectedMatchDays(selectedMatchDays.filter((v) => v !== option))
                    } else {
                      setSelectedMatchDays([...selectedMatchDays, option])
                    }
                  }}>
                  <Checkbox checked={selectedMatchDays.includes(option)} size="medium" />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Selector>
          </div>
        </div>
        <div className="activity-dialog-table">
          <Table
            columns={columns}
            rows={activityList}
            getRowId={(row) => row.idfActivity}
            selectionType={type === REPORT_TYPE.DAILY ? "radio" : "checkbox"}
            selectionElement={
              type === REPORT_TYPE.DAILY ? RadioSelectionElement : CheckboxSelectionElement
            }
            disableColumnResize
            disableColumnMenu
            columnHeaderHeight={41}
            rowHeight={44}
            slots={{
              pagination: Pagination,
            }}
            rowSelectionModel={selectedActivityList}
            onRowClick={onRowClick}
            loading={isLoading || tableLoading}
          />
        </div>
      </div>
    </Dialog>
  )
}
